import { useEffect, useRef, useState } from 'react';
import { useCall } from '../context/CallContext';
import { useAuth } from '../context/AuthContext';
import UserAvatar from './UserAvatar';
import PermissionGate from './PermissionGate';
import { VIDEO_FILTERS } from './VideoFilterPipeline';
import { PRESET_BACKGROUNDS } from './SegmentationEngine';
import {
  PhoneOff,
  Mic,
  MicOff,
  VideoIcon,
  VideoOff,
  SwitchCamera,
  Phone,
  Volume2,
  VolumeX,
  Sparkles,
  X,
  ChevronUp,
  Image,
  Lock,
  Loader2,
  Upload,
} from 'lucide-react';

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Check if user tier has access to premium features */
function getTierAccess(userTier) {
  const t = (userTier || 'free').toLowerCase();
  return {
    blur: t !== 'free', // pro, premium, label
    customBg: t === 'premium' || t === 'label', // premium, label only
  };
}

function FilterSelector({ activeFilter, onSelect, onClose, onSwitchToBg }) {
  return (
    <div className="absolute bottom-28 left-0 right-0 z-20 px-4 animate-in slide-in-from-bottom-4 duration-200" data-testid="filter-selector">
      <div className="bg-background/80 backdrop-blur-xl border border-border rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-3">
            <span className="text-sm font-medium text-white">Filters</span>
            <button onClick={onSwitchToBg} className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1" data-testid="switch-to-bg-btn">
              <Image className="w-3.5 h-3.5" /> Backgrounds
            </button>
          </div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground" data-testid="close-filters-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {VIDEO_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => onSelect(f.id)}
              className={`flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-xl transition-all ${
                activeFilter === f.id
                  ? 'bg-violet-600/30 border border-violet-500/50 ring-1 ring-violet-500/30'
                  : 'bg-muted/30 border border-transparent hover:bg-muted/50'
              }`}
              data-testid={`filter-${f.id}`}
            >
              <span className="text-lg leading-none">{f.icon}</span>
              <span className="text-[10px] text-muted-foreground leading-tight">{f.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BackgroundSelector({ activeBackground, onSelect, onClose, onSwitchToFilters, onUploadImage, access, loading }) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    onUploadImage(url);
  };

  return (
    <div className="absolute bottom-28 left-0 right-0 z-20 px-4 animate-in slide-in-from-bottom-4 duration-200" data-testid="background-selector">
      <div className="bg-background/80 backdrop-blur-xl border border-border rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-3">
            <button onClick={onSwitchToFilters} className="text-sm text-muted-foreground/80 hover:text-foreground flex items-center gap-1" data-testid="switch-to-filters-btn">
              <Sparkles className="w-3.5 h-3.5" /> Filters
            </button>
            <span className="text-sm font-medium text-white">Backgrounds</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground" data-testid="close-bg-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-4 gap-2 text-muted-foreground/80">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading segmentation model...</span>
          </div>
        )}

        <div className="grid grid-cols-4 gap-2">
          {/* None / Off */}
          <button
            onClick={() => onSelect(null)}
            className={`flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-xl transition-all ${
              !activeBackground
                ? 'bg-violet-600/30 border border-violet-500/50'
                : 'bg-muted/30 border border-transparent hover:bg-muted/50'
            }`}
            data-testid="bg-none"
          >
            <div className="w-8 h-8 rounded-lg border border-border flex items-center justify-center">
              <X className="w-4 h-4 text-muted-foreground/70" />
            </div>
            <span className="text-[10px] text-muted-foreground">None</span>
          </button>

          {/* Preset backgrounds */}
          {PRESET_BACKGROUNDS.map(bg => {
            const isActive = activeBackground?.mode === bg.type;
            const locked = !access.blur;

            return (
              <button
                key={bg.id}
                onClick={() => locked ? null : onSelect(bg)}
                disabled={locked}
                className={`flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-xl transition-all relative ${
                  locked ? 'opacity-40 cursor-not-allowed' : ''
                } ${
                  isActive
                    ? 'bg-violet-600/30 border border-violet-500/50'
                    : 'bg-muted/30 border border-transparent hover:bg-muted/50'
                }`}
                data-testid={`bg-${bg.id}`}
              >
                <div className="w-8 h-8 rounded-lg overflow-hidden" style={{ background: bg.preview }} />
                <span className="text-[10px] text-muted-foreground">{bg.label}</span>
                {locked && <Lock className="w-3 h-3 text-amber-400 absolute top-1 right-1" />}
              </button>
            );
          })}

          {/* Custom image upload */}
          <button
            onClick={() => access.customBg ? fileInputRef.current?.click() : null}
            disabled={!access.customBg}
            className={`flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-xl transition-all relative ${
              !access.customBg ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              activeBackground?.mode === 'image'
                ? 'bg-violet-600/30 border border-violet-500/50'
                : 'bg-muted/30 border border-transparent hover:bg-muted/50'
            }`}
            data-testid="bg-custom-upload"
          >
            <div className="w-8 h-8 rounded-lg border border-dashed border-border flex items-center justify-center">
              <Upload className="w-4 h-4 text-muted-foreground/70" />
            </div>
            <span className="text-[10px] text-muted-foreground">Custom</span>
            {!access.customBg && <Lock className="w-3 h-3 text-amber-400 absolute top-1 right-1" />}
          </button>
        </div>

        {/* Tier upgrade hint */}
        {(!access.blur || !access.customBg) && (
          <p className="text-[10px] text-amber-400/70 mt-2 text-center">
            {!access.blur ? 'Upgrade to Pro for background effects' : 'Upgrade to Premium for custom backgrounds'}
          </p>
        )}

        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
      </div>
    </div>
  );
}

function ControlButton({ onClick, active, danger, children, testId, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        onClick={onClick}
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all active:scale-90 ${
          danger
            ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30'
            : active
            ? 'bg-white text-black shadow-lg'
            : 'bg-muted/40 text-white hover:bg-muted/60 backdrop-blur-sm'
        }`}
        data-testid={testId}
      >
        {children}
      </button>
      {label && <span className="text-[10px] text-muted-foreground/70">{label}</span>}
    </div>
  );
}

export default function CallScreen() {
  const {
    callState,
    callType,
    remoteUser,
    isMuted,
    isCameraOff,
    isSpeakerOn,
    callDuration,
    localStream,
    remoteStream,
    endCall,
    toggleMute,
    toggleCamera,
    switchCamera,
    toggleSpeaker,
    activeFilter,
    setActiveFilter,
    activeBackground,
    bgLoading,
    setActiveBackground,
    setCustomBackgroundImage,
    permissionStatus,
    pendingCallType,
    retryPermission,
    cancelPermission,
    filterPipelineRef,
  } = useCall();

  const { user } = useAuth();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [panel, setPanel] = useState(null); // null | 'filters' | 'backgrounds'

  const tierAccess = getTierAccess(user?.artist_tier || user?.subscription_tier);

  // Attach local stream — use filter pipeline canvas if active, else raw stream
  useEffect(() => {
    if (!localVideoRef.current) return;
    if (filterPipelineRef?.current?.filteredStream) {
      localVideoRef.current.srcObject = filterPipelineRef.current.filteredStream;
    } else if (localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, filterPipelineRef, activeFilter, activeBackground]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  if (callState === 'idle' && !permissionStatus) return null;

  if (permissionStatus && permissionStatus !== 'granted' && (callState === 'idle' || permissionStatus === 'denied' || permissionStatus === 'blocked')) {
    return (
      <PermissionGate
        permissionStatus={permissionStatus}
        callType={pendingCallType || callType || 'voice'}
        onRetry={retryPermission}
        onCancel={cancelPermission}
      />
    );
  }

  if (callState === 'idle') return null;

  const isVideo = callType === 'video';
  const isConnected = callState === 'connected';

  const statusLabel = {
    calling: 'Calling',
    ringing: 'Ringing',
    connected: formatDuration(callDuration),
    ended: 'Call Ended',
  }[callState] || 'Connecting';

  const handleBgSelect = (bg) => {
    if (!bg) {
      setActiveBackground(null);
    } else {
      setActiveBackground(bg.type, bg.value);
    }
  };

  const handleBgImageUpload = async (url) => {
    await setCustomBackgroundImage(url);
  };

  /* ─── VIDEO CALL UI ────────────────────────────────────── */
  if (isVideo) {
    return (
      <div className="fixed inset-0 z-[99] flex flex-col bg-black" data-testid="call-screen">
        {/* Remote video (full screen) */}
        <div className="absolute inset-0">
          {remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
              data-testid="remote-video"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-zinc-900 to-black">
              <div className="relative mb-5">
                {!isConnected && (
                  <div className="absolute -inset-4 rounded-full border-2 border-violet-500/40 animate-ping" />
                )}
                <UserAvatar
                  src={remoteUser?.avatar}
                  name={remoteUser?.name || 'User'}
                  size="lg"
                  className="w-28 h-28"
                />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-1" data-testid="call-remote-name">
                {remoteUser?.name || 'Unknown'}
              </h2>
              <p className="text-muted-foreground/70 text-sm flex items-center gap-2" data-testid="call-status">
                {callState === 'calling' && <Phone className="w-3.5 h-3.5 animate-pulse" />}
                {statusLabel}
              </p>
            </div>
          )}
        </div>

        {/* Top bar */}
        {remoteStream && (
          <div className="relative z-10 flex items-center justify-between p-4 pt-12 bg-gradient-to-b from-black/70 via-black/30 to-transparent">
            <div>
              <h3 className="text-foreground font-semibold text-lg drop-shadow" data-testid="call-remote-name">
                {remoteUser?.name || 'Unknown'}
              </h3>
              <p className="text-muted-foreground text-sm drop-shadow" data-testid="call-status">
                {statusLabel}
              </p>
            </div>
            {isConnected && (
              <div className="px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
                <span className="text-xs text-green-400 font-medium">{formatDuration(callDuration)}</span>
              </div>
            )}
          </div>
        )}

        {/* Local video PiP */}
        <div className="absolute top-16 right-4 w-28 h-40 rounded-2xl overflow-hidden border-2 border-border shadow-2xl z-10 bg-card">
          {localStream && !isCameraOff ? (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
              data-testid="local-video"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground/60">
              <VideoOff className="w-6 h-6" />
            </div>
          )}
          {/* Background active indicator */}
          {activeBackground && (
            <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-violet-600/80 rounded text-[8px] text-white">
              BG
            </div>
          )}
        </div>

        {/* Filter/Background selector overlay */}
        {panel === 'filters' && (
          <FilterSelector
            activeFilter={activeFilter}
            onSelect={(id) => setActiveFilter(id)}
            onClose={() => setPanel(null)}
            onSwitchToBg={() => setPanel('backgrounds')}
          />
        )}
        {panel === 'backgrounds' && (
          <BackgroundSelector
            activeBackground={activeBackground}
            onSelect={handleBgSelect}
            onClose={() => setPanel(null)}
            onSwitchToFilters={() => setPanel('filters')}
            onUploadImage={handleBgImageUpload}
            access={tierAccess}
            loading={bgLoading}
          />
        )}

        {/* Controls bar */}
        <div className="relative z-10 mt-auto pb-10 pt-8 px-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
          <div className="flex items-end justify-center gap-4">
            <ControlButton onClick={toggleMute} active={isMuted} testId="toggle-mute-btn" label={isMuted ? 'Unmute' : 'Mute'}>
              {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </ControlButton>

            <ControlButton onClick={toggleCamera} active={isCameraOff} testId="toggle-camera-btn" label={isCameraOff ? 'Camera On' : 'Camera Off'}>
              {isCameraOff ? <VideoOff className="w-6 h-6" /> : <VideoIcon className="w-6 h-6" />}
            </ControlButton>

            <ControlButton onClick={switchCamera} testId="switch-camera-btn" label="Flip">
              <SwitchCamera className="w-6 h-6" />
            </ControlButton>

            <ControlButton onClick={() => setPanel(panel === 'filters' ? null : 'filters')} active={activeFilter !== 'normal'} testId="toggle-filters-btn" label="Filters">
              <Sparkles className="w-6 h-6" />
            </ControlButton>

            <ControlButton onClick={() => setPanel(panel === 'backgrounds' ? null : 'backgrounds')} active={!!activeBackground} testId="toggle-bg-btn" label="BG">
              <Image className="w-6 h-6" />
            </ControlButton>

            <ControlButton onClick={endCall} danger testId="end-call-btn">
              <PhoneOff className="w-7 h-7" />
            </ControlButton>
          </div>
        </div>
      </div>
    );
  }

  /* ─── VOICE CALL UI ───────────────────────────────────── */
  return (
    <div className="fixed inset-0 z-[99] flex flex-col bg-gradient-to-b from-zinc-900 via-zinc-950 to-black" data-testid="call-screen">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-violet-600/15 rounded-full blur-[100px]" />

      <div className="relative z-10 flex items-center justify-center pt-14 pb-4">
        {isConnected && (
          <div className="px-4 py-1.5 bg-green-500/15 border border-green-500/25 rounded-full">
            <span className="text-sm text-green-400 font-mono font-medium" data-testid="call-timer">{formatDuration(callDuration)}</span>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center relative z-10">
        <div className="relative mb-6">
          {isConnected && (
            <>
              <div className="absolute -inset-4 rounded-full bg-green-500/10 animate-pulse" />
              <div className="absolute -inset-6 rounded-full border border-green-500/20 animate-ping" style={{ animationDuration: '2s' }} />
            </>
          )}
          {!isConnected && (
            <div className="absolute -inset-4 rounded-full border-2 border-violet-500/30 animate-ping" style={{ animationDuration: '1.5s' }} />
          )}
          <UserAvatar
            src={remoteUser?.avatar}
            name={remoteUser?.name || 'User'}
            size="lg"
            className="w-28 h-28"
          />
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-1" data-testid="call-remote-name">
          {remoteUser?.name || 'Unknown'}
        </h2>
        <p className="text-muted-foreground/70 text-sm flex items-center gap-2" data-testid="call-status">
          {callState === 'calling' && <Phone className="w-3.5 h-3.5 animate-pulse" />}
          {callState === 'ringing' && <ChevronUp className="w-3.5 h-3.5 animate-bounce" />}
          {statusLabel}
        </p>

        {isConnected && !isMuted && (
          <div className="flex items-center gap-0.5 mt-6" data-testid="audio-indicator">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-green-400/70 rounded-full"
                style={{
                  height: `${12 + Math.random() * 16}px`,
                  animation: `audioWave 0.8s ease-in-out ${i * 0.1}s infinite alternate`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="relative z-10 pb-14 pt-8 px-6">
        <div className="flex items-end justify-center gap-5">
          <ControlButton onClick={toggleMute} active={isMuted} testId="toggle-mute-btn" label={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </ControlButton>

          <ControlButton onClick={toggleSpeaker} active={isSpeakerOn} testId="toggle-speaker-btn" label={isSpeakerOn ? 'Earpiece' : 'Speaker'}>
            {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
          </ControlButton>

          <ControlButton onClick={endCall} danger testId="end-call-btn">
            <PhoneOff className="w-7 h-7" />
          </ControlButton>
        </div>
      </div>

      <style>{`
        @keyframes audioWave {
          from { height: 8px; }
          to { height: 24px; }
        }
      `}</style>
    </div>
  );
}
